import { getModelDisplayName } from "@marketing/home/config/model-display-names";
import { getVideoEtaLabel, getVideoModelLabel } from "@saas/ai-video/types";
import {
	type I2VRowMetadata,
	getI2VModel,
	isI2VHistoryRow,
	resolveI2VHistoryModelId,
} from "@saas/image-to-video/types";

/**
 * 历史记录里显示的模型名。
 *
 * video_generation 表被 ai-video 和 image-to-video 共用，两边还有同一个
 * providerModel（grok-imagine-video-1-5-preview）——光看 `model` 列分不出档位名，
 * 所以先认 metadata.tool，再用 metadata.modelId 查对应注册表。
 */
export function getVideoRowModelLabel(item: {
	model: string;
	metadata?: unknown;
}): string {
	if (isI2VHistoryRow(item.metadata)) {
		const modelId = resolveI2VHistoryModelId(
			(item.metadata as I2VRowMetadata).modelId,
		);
		if (modelId) {
			return getModelDisplayName(modelId);
		}
	}
	return getVideoModelLabel(item.model);
}

/**
 * 「还要等多久」的文案。
 *
 * ai-video 的 `getVideoEtaLabel` 只按分辨率和时长估，因为那一页只有一个模型。
 * image-to-video 有四档，档位之间差一个数量级：2026-09-12 实测同为 720p/10s，
 * minimax-h3 是 1m49s–6m22s，seedance-2 是 3m57s–10m58s。照搬那个公式会对
 * Master 档报出「80–145 秒」，用户等到三分钟就以为卡死了。
 *
 * 所以 i2v 的行按档位给区间，且一律用分钟——秒在这个量级读起来没有意义。
 * 区间取实测低端和高端各留一点余量，上游排队本身就能让同参数差一倍。
 */
const I2V_TIER_ETA_MINUTES: Record<string, [number, number]> = {
	basic: [1, 3],
	fast: [1, 3],
	quality: [2, 7],
	master: [3, 12],
	// Wan 3.0 实测 10s 720p 3m37s–3m51s，30 秒的还要更久
	long: [3, 12],
};

export function getVideoRowEtaLabel(item: {
	model: string;
	resolution: string;
	duration: number;
	metadata?: unknown;
}): string {
	if (isI2VHistoryRow(item.metadata)) {
		const modelId = resolveI2VHistoryModelId(
			(item.metadata as I2VRowMetadata).modelId,
		);
		if (modelId) {
			const [low, high] =
				I2V_TIER_ETA_MINUTES[getI2VModel(modelId).tier] ??
				I2V_TIER_ETA_MINUTES.quality;
			return `Usually takes ${low}–${high} minutes`;
		}
	}
	return getVideoEtaLabel(item.resolution, item.duration);
}
